import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useSearchParams } from 'react-router-dom';
import api from '../config/api';
import toast from 'react-hot-toast';
import { FaUser, FaMotorcycle, FaEdit, FaUsers, FaClipboardList, FaChartLine, FaShieldAlt, FaUniversity, FaRupeeSign, FaWallet, FaHistory, FaCheckCircle, FaClock, FaTimesCircle, FaSpinner, FaMoneyBillWave } from 'react-icons/fa';

// Bank Details Form Component
const BankDetailsForm = ({ user, updateUser }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    accountHolderName: user?.bankDetails?.accountHolderName || '',
    accountNumber: user?.bankDetails?.accountNumber || '',
    confirmAccountNumber: '',
    ifscCode: user?.bankDetails?.ifscCode || '',
    bankName: user?.bankDetails?.bankName || '',
    branchName: user?.bankDetails?.branchName || ''
  });

  useEffect(() => {
    setFormData({
      accountHolderName: user?.bankDetails?.accountHolderName || '',
      accountNumber: user?.bankDetails?.accountNumber || '',
      confirmAccountNumber: '',
      ifscCode: user?.bankDetails?.ifscCode || '',
      bankName: user?.bankDetails?.bankName || '',
      branchName: user?.bankDetails?.branchName || ''
    });
  }, [user]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: name === 'ifscCode' ? value.toUpperCase() : value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate confirm account number
    if (formData.accountNumber !== formData.confirmAccountNumber) {
      toast.error('Account number and confirm account number do not match');
      return;
    }
    
    setLoading(true);

    try {
      // Don't send confirmAccountNumber to server
      const { confirmAccountNumber, ...dataToSend } = formData;
      const response = await api.put('/api/users/bank-details', dataToSend);
      updateUser(response.data.user);
      toast.success('Bank details updated successfully');
      // Clear confirm field after successful submission
      setFormData(prev => ({ ...prev, confirmAccountNumber: '' }));
    } catch (error) {
      console.error('Update bank details error:', error);
      const message = error.response?.data?.message || 'Failed to update bank details';
      const errors = error.response?.data?.errors;
      if (errors && errors.length > 0) {
        toast.error(errors[0].msg || message);
      } else {
        toast.error(message);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      {user?.bankDetails?.accountNumber ? (
        <div className="mb-6 p-4 bg-green-50 rounded-lg border border-green-200">
          <div className="flex items-center mb-3">
            <FaUniversity className="text-green-600 mr-2" />
            <span className="font-semibold text-green-800">Bank Details Added</span>
          </div>
          <div className="text-sm text-gray-700 space-y-2">
            <p><strong>Account Holder:</strong> {user.bankDetails.accountHolderName}</p>
            <p><strong>Account Number:</strong> {user.bankDetails.accountNumber.replace(/(.{4})/g, '$1 ').trim()}</p>
            <p><strong>IFSC Code:</strong> {user.bankDetails.ifscCode}</p>
            <p><strong>Bank:</strong> {user.bankDetails.bankName}</p>
            {user.bankDetails.branchName && (
              <p><strong>Branch:</strong> {user.bankDetails.branchName}</p>
            )}
          </div>
        </div>
      ) : (
        <div className="mb-6 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
          <p className="text-yellow-800 text-sm">
            Please add your bank details to receive payouts and request withdrawals.
          </p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Account Holder Name *
          </label>
          <input
            type="text"
            name="accountHolderName"
            value={formData.accountHolderName}
            onChange={handleChange}
            className="input-field"
            placeholder="Enter account holder name"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Account Number *
          </label>
          <input
            type="text"
            name="accountNumber"
            value={formData.accountNumber}
            onChange={handleChange}
            className="input-field"
            placeholder="Enter account number"
            required
            minLength={9}
            maxLength={18}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Confirm Account Number *
          </label>
          <input
            type="text"
            name="confirmAccountNumber"
            value={formData.confirmAccountNumber}
            onChange={handleChange}
            className="input-field"
            placeholder="Re-enter account number to confirm"
            required
            minLength={9}
            maxLength={18}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            IFSC Code *
          </label>
          <input
            type="text"
            name="ifscCode"
            value={formData.ifscCode}
            onChange={handleChange}
            className="input-field uppercase"
            placeholder="e.g., HDFC0001234"
            required
            minLength={11}
            maxLength={11}
            pattern="[A-Z]{4}0[A-Z0-9]{6}"
          />
          <p className="text-xs text-gray-500 mt-1">
            Format: 4 letters + 0 + 6 alphanumeric characters
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Bank Name *
          </label>
          <input
            type="text"
            name="bankName"
            value={formData.bankName}
            onChange={handleChange}
            className="input-field"
            placeholder="Enter bank name"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Branch Name (Optional)
          </label>
          <input
            type="text"
            name="branchName"
            value={formData.branchName}
            onChange={handleChange}
            className="input-field"
            placeholder="Enter branch name"
          />
        </div>

        <button
          type="submit"
          disabled={loading}
          className="btn-primary"
        >
          {loading ? 'Saving...' : user?.bankDetails?.accountNumber ? 'Update Bank Details' : 'Save Bank Details'}
        </button>
      </form>
    </div>
  );
};

// Vehicle Details Form Component
const VehicleDetailsForm = ({ user, updateUser }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    type: user?.vehicleInfo?.type || 'bike',
    model: user?.vehicleInfo?.model || '',
    year: user?.vehicleInfo?.year || '',
    color: user?.vehicleInfo?.color || '',
    plateNumber: user?.vehicleInfo?.plateNumber || '',
    seats: user?.vehicleInfo?.seats || ''
  });

  useEffect(() => {
    setFormData({
      type: user?.vehicleInfo?.type || 'bike',
      model: user?.vehicleInfo?.model || '',
      year: user?.vehicleInfo?.year || '',
      color: user?.vehicleInfo?.color || '',
      plateNumber: user?.vehicleInfo?.plateNumber || '',
      seats: user?.vehicleInfo?.seats || ''
    });
  }, [user]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const vehicleInfo = {
        type: formData.type,
        model: formData.model,
        year: formData.year,
        color: formData.color,
        plateNumber: formData.plateNumber,
        ...(formData.seats && { seats: formData.seats })
      };

      const response = await api.put('/api/users/vehicle-details', { vehicleInfo });
      updateUser(response.data.user);
      toast.success('Vehicle details updated successfully');
    } catch (error) {
      console.error('Update vehicle details error:', error);
      const message = error.response?.data?.message || 'Failed to update vehicle details';
      const errors = error.response?.data?.errors;
      if (errors && errors.length > 0) {
        toast.error(errors[0].msg || message);
      } else {
        toast.error(message);
      }
    } finally {
      setLoading(false);
    }
  };

  const isFourWheeler = ['car', 'sedan', 'hatchback', 'suv'].includes(formData.type);

  return (
    <div>
      {!user?.vehicleInfo ? (
        <div className="mb-6 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
          <p className="text-yellow-800 text-sm">
            Please add your vehicle details. You can update them anytime from here.
          </p>
        </div>
      ) : null}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Vehicle Type *
          </label>
          <select
            name="type"
            value={formData.type}
            onChange={handleChange}
            className="input-field"
            required
          >
            <optgroup label="Two Wheelers">
              <option value="bike">Bike</option>
              <option value="scooter">Scooter</option>
            </optgroup>
            <optgroup label="Four Wheelers">
              <option value="car">Car</option>
              <option value="sedan">Sedan</option>
              <option value="hatchback">Hatchback</option>
              <option value="suv">SUV</option>
            </optgroup>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Vehicle Model *
          </label>
          <input
            type="text"
            name="model"
            value={formData.model}
            onChange={handleChange}
            className="input-field"
            placeholder="e.g., Honda Activa, Maruti Swift"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Year *
          </label>
          <input
            type="number"
            name="year"
            value={formData.year}
            onChange={handleChange}
            className="input-field"
            placeholder="e.g., 2020"
            min="1990"
            max={new Date().getFullYear()}
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Color *
          </label>
          <input
            type="text"
            name="color"
            value={formData.color}
            onChange={handleChange}
            className="input-field"
            placeholder="e.g., Red, Blue, White"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Plate Number *
          </label>
          <input
            type="text"
            name="plateNumber"
            value={formData.plateNumber}
            onChange={handleChange}
            className="input-field uppercase"
            placeholder="e.g., MH12AB1234"
            required
          />
        </div>

        {isFourWheeler && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Number of Seats (excluding driver) *
            </label>
            <input
              type="number"
              name="seats"
              value={formData.seats}
              onChange={handleChange}
              className="input-field"
              min="1"
              max="10"
              required={isFourWheeler}
            />
            <p className="text-xs text-gray-500 mt-1">
              Number of passenger seats your vehicle can accommodate
            </p>
          </div>
        )}

        <button
          type="submit"
          disabled={loading}
          className="btn-primary"
        >
          {loading ? 'Saving...' : user?.vehicleInfo ? 'Update Vehicle Details' : 'Save Vehicle Details'}
        </button>
      </form>
    </div>
  );
};

// Earnings Tab Component
const EarningsTab = ({ user }) => {
  const [earnings, setEarnings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchEarnings();
  }, []);

  const fetchEarnings = async () => {
    try {
      const response = await api.get('/api/payments/rider/earnings');
      setEarnings(response.data);
    } catch (error) {
      console.error('Fetch earnings error:', error);
      toast.error('Failed to fetch earnings');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <FaSpinner className="inline mr-2 animate-spin text-2xl text-blue-600" />
        <p className="mt-2 text-gray-600">Loading earnings...</p>
      </div>
    );
  }

  if (!earnings) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">No earnings data available</p>
      </div>
    );
  }

  return (
    <div>
      {/* Earnings Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="card bg-gradient-to-r from-green-50 to-green-100 border-2 border-green-200">
          <div className="text-center py-4">
            <FaMoneyBillWave className="text-3xl text-green-600 mx-auto mb-2" />
            <p className="text-sm text-gray-600 mb-1">Total Earnings</p>
            <p className="text-2xl font-bold text-green-700">
              ₹{earnings.earnings.total.toFixed(2)}
            </p>
          </div>
        </div>

        <div className="card bg-gradient-to-r from-blue-50 to-blue-100 border-2 border-blue-200">
          <div className="text-center py-4">
            <FaChartLine className="text-3xl text-blue-600 mx-auto mb-2" />
            <p className="text-sm text-gray-600 mb-1">This Month</p>
            <p className="text-2xl font-bold text-blue-700">
              ₹{earnings.earnings.thisMonth.toFixed(2)}
            </p>
          </div>
        </div>

        <div className="card bg-gradient-to-r from-purple-50 to-purple-100 border-2 border-purple-200">
          <div className="text-center py-4">
            <FaClock className="text-3xl text-purple-600 mx-auto mb-2" />
            <p className="text-sm text-gray-600 mb-1">This Week</p>
            <p className="text-2xl font-bold text-purple-700">
              ₹{earnings.earnings.thisWeek.toFixed(2)}
            </p>
          </div>
        </div>

        <div className="card bg-gradient-to-r from-yellow-50 to-yellow-100 border-2 border-yellow-200">
          <div className="text-center py-4">
            <FaWallet className="text-3xl text-yellow-600 mx-auto mb-2" />
            <p className="text-sm text-gray-600 mb-1">Pending Cash</p>
            <p className="text-2xl font-bold text-yellow-700">
              ₹{earnings.earnings.pending.toFixed(2)}
            </p>
          </div>
        </div>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="card">
          <div className="text-center py-4">
            <p className="text-sm text-gray-600 mb-1">Total Rides</p>
            <p className="text-2xl font-bold text-gray-800">
              {earnings.stats.totalRides}
            </p>
          </div>
        </div>

        <div className="card">
          <div className="text-center py-4">
            <p className="text-sm text-gray-600 mb-1">Platform Fees Paid</p>
            <p className="text-2xl font-bold text-gray-800">
              ₹{earnings.stats.totalPlatformFees.toFixed(2)}
            </p>
          </div>
        </div>

        <div className="card">
          <div className="text-center py-4">
            <p className="text-sm text-gray-600 mb-1">Average per Ride</p>
            <p className="text-2xl font-bold text-gray-800">
              ₹{earnings.stats.averagePerRide.toFixed(2)}
            </p>
          </div>
        </div>
      </div>

      {/* Pending Cash Payments */}
      {earnings.pendingPayments && earnings.pendingPayments.length > 0 && (
        <div className="card mb-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
            <FaClock className="mr-2 text-yellow-600" />
            Pending Cash Payments ({earnings.pendingPayments.length})
          </h3>
          <div className="space-y-3">
            {earnings.pendingPayments.map((payment) => (
              <div
                key={payment._id}
                className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg"
              >
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-semibold text-gray-800">
                      {payment.passenger?.name || 'Unknown'}
                    </p>
                    {payment.booking?.trip && (
                      <p className="text-sm text-gray-600">
                        {payment.booking.trip.origin?.address} → {payment.booking.trip.destination?.address}
                      </p>
                    )}
                    <p className="text-xs text-gray-500 mt-1">
                      {formatDate(payment.createdAt)}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold text-yellow-700">
                      ₹{payment.riderAmount?.toFixed(2) || '0.00'}
                    </p>
                    <span className="badge badge-warning text-xs">Pending</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent Payments */}
      {earnings.recentPayments && earnings.recentPayments.length > 0 && (
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
            <FaHistory className="mr-2 text-blue-600" />
            Recent Payments ({earnings.recentPayments.length})
          </h3>
          <div className="space-y-3">
            {earnings.recentPayments.map((payment) => (
              <div
                key={payment._id}
                className="p-4 bg-gray-50 border border-gray-200 rounded-lg"
              >
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-semibold text-gray-800">
                      {payment.passenger?.name || 'Unknown'}
                    </p>
                    {payment.booking?.trip && (
                      <p className="text-sm text-gray-600">
                        {payment.booking.trip.origin?.address} → {payment.booking.trip.destination?.address}
                      </p>
                    )}
                    <p className="text-xs text-gray-500 mt-1">
                      {formatDate(payment.processedAt || payment.createdAt)}
                    </p>
                    <p className="text-xs text-gray-500">
                      Method: {payment.paymentMethod || 'N/A'}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold text-green-700">
                      ₹{payment.riderAmount?.toFixed(2) || '0.00'}
                    </p>
                    <span className="badge badge-success text-xs">Completed</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {earnings.recentPayments && earnings.recentPayments.length === 0 && (!earnings.pendingPayments || earnings.pendingPayments.length === 0) && (
        <div className="card text-center py-12">
          <FaMoneyBillWave className="text-4xl text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">No payments yet</p>
          <p className="text-sm text-gray-500 mt-2">Start offering rides to earn money!</p>
        </div>
      )}
    </div>
  );
};

// Wallet Tab Component
const WalletTab = ({ user, updateUser }) => {
  const [withdrawalAmount, setWithdrawalAmount] = useState('');
  const [withdrawalNotes, setWithdrawalNotes] = useState('');
  const [withdrawalLoading, setWithdrawalLoading] = useState(false);
  const [withdrawals, setWithdrawals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showWithdrawalModal, setShowWithdrawalModal] = useState(false);

  useEffect(() => {
    fetchWithdrawals();
  }, []);

  // Auto-refresh user data when wallet tab is active
  useEffect(() => {
    if (!user || user.role !== 'rider') return;

    // Refresh user data every 20 seconds to reduce API calls
    // Only refresh when wallet tab is visible (using Page Visibility API)
    let refreshInterval = null;
    
    const refreshUserData = async () => {
      // Only refresh if page is visible
      if (document.visibilityState === 'visible') {
        try {
          const response = await api.get('/api/users/profile');
          updateUser(response.data.user);
        } catch (error) {
          // Handle rate limit errors
          if (error.response?.status === 429) {
            console.warn('Rate limited on wallet refresh. Slowing down...');
            // Clear interval and wait longer
            if (refreshInterval) {
              clearInterval(refreshInterval);
              refreshInterval = null;
            }
            // Retry after 60 seconds
            setTimeout(() => {
              refreshInterval = setInterval(refreshUserData, 60000); // 60 seconds after rate limit
            }, 60000);
          } else {
            console.error('Error refreshing user data:', error);
          }
        }
      }
    };

    // Initial refresh
    refreshUserData();
    
    // Set up interval - refresh every 60 seconds to prevent rate limiting
    refreshInterval = setInterval(refreshUserData, 60000);

    // Cleanup on unmount or when user changes
    return () => {
      if (refreshInterval) {
        clearInterval(refreshInterval);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.role]);

  const fetchWithdrawals = async () => {
    try {
      const response = await api.get('/api/payouts/my-requests');
      setWithdrawals(response.data.payouts);
    } catch (error) {
      console.error('Fetch withdrawals error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleWithdrawalRequest = async () => {
    if (!withdrawalAmount || parseFloat(withdrawalAmount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    const amount = parseFloat(withdrawalAmount);
    const walletBalance = user?.wallet?.balance || 0;
    const pendingPlatformFees = user?.wallet?.pendingPlatformFees || 0;
    const availableBalance = Math.max(0, walletBalance - pendingPlatformFees);

    if (amount > availableBalance) {
      toast.error(`Insufficient available balance. Available: ₹${availableBalance.toFixed(2)}`);
      return;
    }

    if (amount < 100) {
      toast.error('Minimum withdrawal amount is ₹100');
      return;
    }

    setWithdrawalLoading(true);
    try {
      const response = await api.post('/api/payouts/request-withdrawal', {
        amount,
        notes: withdrawalNotes
      });

      toast.success(`Withdrawal request of ₹${amount} submitted successfully`);
      setShowWithdrawalModal(false);
      setWithdrawalAmount('');
      setWithdrawalNotes('');
      
      // Refresh user data and withdrawals
      const userResponse = await api.get('/api/users/profile');
      updateUser(userResponse.data.user);
      fetchWithdrawals();
    } catch (error) {
      console.error('Withdrawal request error:', error);
      const message = error.response?.data?.message || 'Failed to submit withdrawal request';
      toast.error(message);
    } finally {
      setWithdrawalLoading(false);
    }
  };

  const handleCancelWithdrawal = async (payoutId) => {
    if (!window.confirm('Are you sure you want to cancel this withdrawal request?')) {
      return;
    }

    try {
      await api.put(`/api/payouts/${payoutId}/cancel`);
      toast.success('Withdrawal request cancelled');
      fetchWithdrawals();
      
      // Refresh user data
      const userResponse = await api.get('/api/users/profile');
      updateUser(userResponse.data.user);
    } catch (error) {
      console.error('Cancel withdrawal error:', error);
      toast.error('Failed to cancel withdrawal request');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      requested: 'badge-warning',
      pending: 'badge-warning',
      processing: 'badge-info',
      completed: 'badge-success',
      failed: 'badge-danger',
      cancelled: 'badge-secondary',
      rejected: 'badge-danger'
    };

    const icons = {
      requested: <FaClock className="inline mr-1" />,
      pending: <FaClock className="inline mr-1" />,
      processing: <FaSpinner className="inline mr-1 animate-spin" />,
      completed: <FaCheckCircle className="inline mr-1" />,
      failed: <FaTimesCircle className="inline mr-1" />,
      cancelled: <FaTimesCircle className="inline mr-1" />,
      rejected: <FaTimesCircle className="inline mr-1" />
    };

    return (
      <span className={`badge ${badges[status] || 'badge-secondary'}`}>
        {icons[status]}
        {status.charAt(0).toUpperCase() + status.slice(1).replace('_', ' ')}
      </span>
    );
  };

  const pendingWithdrawal = withdrawals.find(w => ['requested', 'pending', 'processing'].includes(w.status));
  const walletBalance = user?.wallet?.balance || 0;
  const pendingPlatformFees = user?.wallet?.pendingPlatformFees || 0;
  const availableBalance = Math.max(0, walletBalance - pendingPlatformFees);

  return (
    <div>
      {/* Wallet Balance Card */}
      <div className="bg-green-50 rounded-lg border border-green-200 mb-4 sm:mb-6">
        <div className="text-center py-6 px-4">
          <FaWallet className="text-4xl text-green-600 mx-auto mb-3" />
          <div className="text-4xl font-bold text-green-600 mb-2">
            ₹{availableBalance.toFixed(2)}
          </div>
          <p className="text-sm text-gray-600 mb-4">Available Balance</p>
          <button
            onClick={() => setShowWithdrawalModal(true)}
            disabled={!user?.bankDetails?.accountNumber || availableBalance < 100}
            className={`bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-6 rounded-lg transition-colors flex items-center justify-center mx-auto ${
              !user?.bankDetails?.accountNumber || availableBalance < 100
                ? 'opacity-50 cursor-not-allowed'
                : ''
            }`}
          >
            <FaRupeeSign className="inline mr-2" />
            Request Withdrawal
          </button>
          {!user?.bankDetails?.accountNumber && (
            <p className="text-xs text-gray-500 mt-3">
              Please add bank details first
            </p>
          )}
          {availableBalance < 100 && user?.bankDetails?.accountNumber && (
            <p className="text-xs text-gray-500 mt-3">
              Minimum withdrawal amount is ₹100
            </p>
          )}
        </div>
      </div>

      {/* Pending Withdrawal Alert */}
      {pendingWithdrawal && (
        <div className="card mb-6 bg-yellow-50 border border-yellow-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-semibold text-yellow-800 mb-1">Pending Withdrawal Request</p>
              <p className="text-sm text-gray-700">
                Amount: <span className="font-semibold">₹{pendingWithdrawal.amount.toFixed(2)}</span>
                {' • '}
                Status: {getStatusBadge(pendingWithdrawal.status)}
              </p>
              <p className="text-xs text-gray-600 mt-1">
                Requested on: {new Date(pendingWithdrawal.createdAt).toLocaleString()}
              </p>
            </div>
            {['requested', 'pending'].includes(pendingWithdrawal.status) && (
              <button
                onClick={() => handleCancelWithdrawal(pendingWithdrawal._id)}
                className="btn-secondary text-sm"
              >
                Cancel
              </button>
            )}
          </div>
        </div>
      )}

      {/* Transaction History - Combined Wallet and Payout Transactions */}
      <div className="card mb-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
          <FaHistory className="mr-2" />
          Transaction History
        </h3>
        {loading ? (
          <div className="text-center py-8">
            <FaSpinner className="inline animate-spin text-2xl text-gray-400" />
          </div>
        ) : (
          <div className="space-y-3">
            {(() => {
              // Combine wallet transactions and payout transactions
              const allTransactions = [];
              
              // Add wallet transactions
              if (user?.wallet?.transactions?.length > 0) {
                user.wallet.transactions.forEach((transaction, index) => {
                  // Determine transaction type from description
                  let transactionType = 'Payment for booking';
                  if (transaction.description?.includes('Payout refund')) {
                    transactionType = 'Payout refund';
                  } else if (transaction.description?.includes('Payout')) {
                    transactionType = 'Payout';
                  } else if (transaction.description?.includes('Bulk payout')) {
                    transactionType = 'Bulk payout';
                  } else if (transaction.description?.includes('Payment for booking')) {
                    transactionType = 'Payment for booking';
                  } else if (transaction.type === 'debit') {
                    transactionType = 'Debit';
                  }
                  
                  // Extract transaction ID from description if available
                  let transactionId = `WALLET_${new Date(transaction.date).getTime()}_${index}`;
                  const payoutMatch = transaction.description?.match(/PAYOUT_[a-z0-9_]+/i);
                  if (payoutMatch) {
                    transactionId = payoutMatch[0];
                  } else {
                    // Try to extract booking ID or other ID from description
                    const idMatch = transaction.description?.match(/[a-f0-9]{24}/i);
                    if (idMatch) {
                      transactionId = idMatch[0];
                    }
                  }
                  
                  allTransactions.push({
                    id: transactionId,
                    type: transactionType,
                    description: transaction.description,
                    amount: transaction.amount,
                    isCredit: transaction.type === 'credit',
                    date: transaction.date,
                    source: 'wallet'
                  });
                });
              }
              
              // Add payout transactions
              withdrawals.forEach((withdrawal) => {
                // Add payout transaction
                allTransactions.push({
                  id: withdrawal.transactionId || withdrawal._id,
                  type: withdrawal.bulkPayout ? 'Bulk payout' : 'Payout',
                  description: `Payout to ${withdrawal.bankDetails?.accountNumber?.slice(-4) || 'bank account'}`,
                  amount: withdrawal.amount,
                  isCredit: false,
                  date: withdrawal.createdAt,
                  source: 'payout',
                  status: withdrawal.status,
                  payoutId: withdrawal._id
                });
                
                // Add payout refund if status is cancelled/rejected
                if (['cancelled', 'rejected', 'failed'].includes(withdrawal.status)) {
                  allTransactions.push({
                    id: `${withdrawal.transactionId || withdrawal._id}_REFUND`,
                    type: 'Payout refund',
                    description: `Refund for ${withdrawal.bulkPayout ? 'bulk payout' : 'payout'}`,
                    amount: withdrawal.amount,
                    isCredit: true,
                    date: withdrawal.updatedAt || withdrawal.createdAt,
                    source: 'payout_refund',
                    payoutId: withdrawal._id
                  });
                }
              });
              
              // Sort by date (newest first)
              allTransactions.sort((a, b) => new Date(b.date) - new Date(a.date));
              
              if (allTransactions.length === 0) {
                return (
                  <p className="text-gray-500 text-center py-8">No transactions yet</p>
                );
              }
              
              return allTransactions.map((transaction) => (
                <div
                  key={transaction.id}
                  className="bg-gray-100 rounded-lg p-4 flex items-start justify-between"
                >
                  <div className="flex-1">
                    <p className="font-medium text-gray-800 text-sm sm:text-base mb-1">
                      {transaction.type}
                    </p>
                    <p className="text-xs text-gray-600 font-mono mb-1 break-all">
                      {transaction.id}
                    </p>
                    <p className="text-xs text-gray-500">
                      {new Date(transaction.date).toLocaleString('en-IN', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                        second: '2-digit'
                      })}
                    </p>
                  </div>
                  <div className="ml-4 text-right">
                    <p
                      className={`text-base sm:text-lg font-semibold ${
                        transaction.isCredit ? 'text-green-600' : 'text-red-600'
                      }`}
                    >
                      {transaction.isCredit ? '+' : '-'}₹{transaction.amount.toFixed(2)}
                    </p>
                    {transaction.source === 'payout' && 
                     ['requested', 'pending'].includes(transaction.status) && (
                      <button
                        onClick={() => handleCancelWithdrawal(transaction.payoutId)}
                        className="text-xs text-red-600 hover:text-red-800 mt-1"
                      >
                        Cancel
                      </button>
                    )}
                  </div>
                </div>
              ));
            })()}
          </div>
        )}
      </div>

      {/* Withdrawal Modal */}
      {showWithdrawalModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-xl font-semibold text-gray-800 mb-4">Request Withdrawal</h3>
            
            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-2">Available Balance:</p>
              <p className="text-2xl font-bold text-green-600">₹{availableBalance.toFixed(2)}</p>
              {pendingPlatformFees > 0 && (
                <p className="text-xs text-gray-500 mt-1">
                  Total: ₹{walletBalance.toFixed(2)} | Pending Fees: ₹{pendingPlatformFees.toFixed(2)}
                </p>
              )}
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Withdrawal Amount (Min: ₹100)
              </label>
              <input
                type="number"
                value={withdrawalAmount}
                onChange={(e) => setWithdrawalAmount(e.target.value)}
                max={availableBalance}
                min={100}
                step="0.01"
                className="input-field"
                placeholder="Enter amount"
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Notes (Optional)
              </label>
              <textarea
                value={withdrawalNotes}
                onChange={(e) => setWithdrawalNotes(e.target.value)}
                className="input-field"
                rows="3"
                placeholder="Add any notes..."
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowWithdrawalModal(false);
                  setWithdrawalAmount('');
                  setWithdrawalNotes('');
                }}
                className="btn-secondary flex-1"
                disabled={withdrawalLoading}
              >
                Cancel
              </button>
              <button
                onClick={handleWithdrawalRequest}
                disabled={withdrawalLoading || !withdrawalAmount || parseFloat(withdrawalAmount) < 100}
                className="btn-primary flex-1"
              >
                {withdrawalLoading ? (
                  <>
                    <FaSpinner className="inline mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  'Submit Request'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Profile = () => {
  const { user, updateUser } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState(tabParam || 'profile');
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: user?.name || '',
    phone: user?.phone || '',
    photo: user?.photo || ''
  });

  // Update active tab when URL parameter changes
  useEffect(() => {
    if (tabParam) {
      setActiveTab(tabParam);
    } else {
      // If no tab param and we're on profile page, default to 'profile'
      setActiveTab('profile');
    }
  }, [tabParam]);

  useEffect(() => {
    setFormData({
      name: user?.name || '',
      phone: user?.phone || '',
      photo: user?.photo || ''
    });
  }, [user]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await api.put('/api/users/profile', formData);
      updateUser(response.data.user);
      toast.success('Profile updated successfully');
    } catch (error) {
      console.error('Update profile error:', error);
      toast.error('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('photo', file);

    try {
      const response = await api.post('/api/users/upload-photo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      updateUser(response.data.user);
      toast.success('Photo updated successfully');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload photo');
    }
  };

  const normalizeFileUrl = (p) => {
    if (!p) return '';
    let cleaned = p.replace(/\\/g, '/');
    if (!cleaned.startsWith('/')) cleaned = '/' + cleaned;
    return cleaned;
  };

  const tabs = [
    { id: 'profile', label: 'Profile', icon: FaUser },
    { id: 'edit', label: 'Edit Profile', icon: FaEdit },
    ...(user?.role === 'rider' ? [
      { id: 'vehicle', label: 'Vehicle Details', icon: FaMotorcycle },
      { id: 'bank', label: 'Bank Details', icon: FaUniversity },
      { id: 'earnings', label: 'Earnings', icon: FaMoneyBillWave },
      { id: 'wallet', label: 'Wallet', icon: FaWallet }
    ] : []),
    ...(user?.role === 'admin' ? [
      { id: 'admin', label: 'Admin Panel', icon: FaShieldAlt }
    ] : [])
  ];

  return (
    <div className="min-h-screen bg-gray-50 pb-20 md:pb-8">
      {/* Mobile Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10 md:hidden">
        <div className="px-4 py-4">
          <h1 className="text-xl font-bold text-gray-900">Profile</h1>
        </div>
      </div>

      <div className="container mx-auto px-4 py-4 md:py-8">
        <div className="max-w-6xl mx-auto">
          <h1 className="hidden md:block text-3xl font-bold text-gray-800 mb-8">Profile</h1>

          {/* Mobile: Vertical Tab List | Desktop: Horizontal Tabs */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
            {/* Mobile: Vertical Tab Navigation */}
            <div className="md:hidden">
              <nav className="flex flex-col">
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => {
                        setActiveTab(tab.id);
                        // Update URL without page reload
                        const newSearchParams = new URLSearchParams(searchParams);
                        newSearchParams.set('tab', tab.id);
                        setSearchParams(newSearchParams, { replace: true });
                      }}
                      className={`flex items-center px-4 py-4 text-base font-medium border-l-4 transition-colors ${
                        activeTab === tab.id
                          ? 'border-blue-600 text-blue-600 bg-blue-50'
                          : 'border-transparent text-gray-700 hover:bg-gray-50 active:bg-gray-100'
                      }`}
                    >
                      <Icon className="mr-3 text-lg" />
                      {tab.label}
                    </button>
                  );
                })}
              </nav>
            </div>

            {/* Desktop: Horizontal Tab Navigation */}
            <div className="hidden md:block border-b border-gray-200">
              <nav className="flex overflow-x-auto">
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`flex items-center px-6 py-4 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                        activeTab === tab.id
                          ? 'border-blue-600 text-blue-600'
                          : 'border-transparent text-gray-600 hover:text-gray-800 hover:border-gray-300'
                      }`}
                    >
                      <Icon className="mr-2" />
                      {tab.label}
                    </button>
                  );
                })}
              </nav>
            </div>

            {/* Tab Content */}
            <div className="p-4 md:p-6">
              {activeTab === 'profile' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Profile Info */}
                  <div className="space-y-4">
                    <div className="text-center">
                  <img
                    src={normalizeFileUrl(user?.photo) || '/default-avatar.png'}
                    alt={user?.name}
                        className="w-32 h-32 rounded-full mx-auto mb-4 border-4 border-gray-200"
                      />
                      <h2 className="text-2xl font-semibold text-gray-800 mb-2">{user?.name}</h2>
                      <p className="text-gray-600 mb-1">{user?.email}</p>
                <p className="text-gray-600 mb-4">{user?.phone}</p>
                
                      <div className="flex justify-center gap-2 mb-4">
                  {user?.role === 'admin' ? (
                    <span className="badge badge-danger">
                      <FaShieldAlt className="inline mr-1" />
                      Admin
                    </span>
                  ) : (
                    <>
                      <span className={`badge ${
                        user?.role === 'rider' ? 'badge-info' : 'badge-success'
                      }`}>
                        {user?.role === 'rider' ? 'Rider' : 'Passenger'}
                      </span>
                      {user?.role === 'rider' && (
                        <span className={`badge ${
                          user?.verificationStatus === 'verified' ? 'badge-success' : 'badge-warning'
                        }`}>
                                {user?.verificationStatus === 'verified' ? 'Verified' : 'Pending'}
                        </span>
                      )}
                    </>
                  )}
                      </div>
                </div>

                    {/* Stats */}
                    <div className="card">
                      <h3 className="text-lg font-semibold text-gray-800 mb-4">Statistics</h3>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="text-center p-4 bg-blue-50 rounded-lg">
                          <div className="text-2xl font-bold text-blue-600">
                            {user?.rating?.toFixed(1) || '0.0'}
                          </div>
                          <p className="text-sm text-gray-600">Rating</p>
                        </div>
                        <div className="text-center p-4 bg-green-50 rounded-lg">
                          <div className="text-2xl font-bold text-green-600">
                            {user?.totalRides || 0}
                          </div>
                          <p className="text-sm text-gray-600">Total Rides</p>
                        </div>
                </div>
                    </div>
                  </div>

                  {/* Additional Info */}
                  <div className="space-y-4">
                  {user?.role === 'rider' && user?.vehicleInfo && (
                      <div className="card">
                        <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                          <FaMotorcycle className="mr-2" />
                          Vehicle Information
                        </h3>
                        <div className="text-sm text-gray-700 space-y-2">
                          <div className="flex justify-between">
                            <span className="text-gray-600">Type:</span>
                            <span className="font-medium capitalize">{user.vehicleInfo.type}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Model:</span>
                            <span className="font-medium">{user.vehicleInfo.model}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Year:</span>
                            <span className="font-medium">{user.vehicleInfo.year}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Color:</span>
                            <span className="font-medium">{user.vehicleInfo.color}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Plate Number:</span>
                            <span className="font-medium">{user.vehicleInfo.plateNumber}</span>
                          </div>
                          {user.vehicleInfo.seats && (
                            <div className="flex justify-between">
                              <span className="text-gray-600">Seats:</span>
                              <span className="font-medium">{user.vehicleInfo.seats}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {user?.role === 'rider' && user?.wallet && (
                      <div className="card bg-gradient-to-r from-green-50 to-blue-50">
                        <h3 className="text-lg font-semibold text-gray-800 mb-2 flex items-center">
                          <FaWallet className="mr-2" />
                          Wallet Balance
                        </h3>
                        <div className="text-3xl font-bold text-green-600">
                          ₹{user.wallet.balance.toFixed(2)}
                      </div>
                    </div>
                  )}
                  </div>
                </div>
              )}

              {activeTab === 'edit' && (
                <div className="max-w-2xl">
                  <div className="mb-6 text-center">
                    <img
                      src={normalizeFileUrl(user?.photo) || '/default-avatar.png'}
                      alt={user?.name}
                      className="w-24 h-24 rounded-full mx-auto mb-4 border-4 border-gray-200"
                    />
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleFileUpload}
                      className="hidden"
                      id="photo-upload"
                    />
                    <label
                      htmlFor="photo-upload"
                      className="btn-secondary cursor-pointer inline-block"
                    >
                      Change Photo
                    </label>
            </div>
                
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Full Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      className="input-field"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Phone Number *
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="input-field"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address
                    </label>
                    <input
                      type="email"
                      value={user?.email}
                      className="input-field bg-gray-100 cursor-not-allowed"
                      disabled
                    />
                    <p className="text-sm text-gray-500 mt-1">
                      Email cannot be changed
                    </p>
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="btn-primary"
                  >
                    {loading ? 'Updating...' : 'Update Profile'}
                  </button>
                </form>
              </div>
              )}

              {activeTab === 'vehicle' && user?.role === 'rider' && (
                <div className="max-w-2xl">
                  <VehicleDetailsForm user={user} updateUser={updateUser} />
                </div>
              )}

              {activeTab === 'bank' && user?.role === 'rider' && (
                <div className="max-w-2xl">
                  <BankDetailsForm user={user} updateUser={updateUser} />
                      </div>
              )}

              {activeTab === 'earnings' && user?.role === 'rider' && (
                <div>
                  <EarningsTab user={user} />
                </div>
              )}

              {activeTab === 'wallet' && user?.role === 'rider' && (
                <div>
                  <WalletTab user={user} updateUser={updateUser} />
                      </div>
              )}

              {activeTab === 'admin' && user?.role === 'admin' && (
                <div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                    <a href="/admin" className="card hover:shadow-lg transition-shadow text-center">
                      <FaChartLine className="text-3xl text-blue-600 mx-auto mb-2" />
                      <p className="font-semibold">Dashboard</p>
                    </a>
                    <a href="/admin/users" className="card hover:shadow-lg transition-shadow text-center">
                      <FaUsers className="text-3xl text-green-600 mx-auto mb-2" />
                      <p className="font-semibold">Users</p>
                    </a>
                    <a href="/admin/trips" className="card hover:shadow-lg transition-shadow text-center">
                      <FaMotorcycle className="text-3xl text-purple-600 mx-auto mb-2" />
                      <p className="font-semibold">Trips</p>
                    </a>
                    <a href="/admin/bookings" className="card hover:shadow-lg transition-shadow text-center">
                      <FaClipboardList className="text-3xl text-orange-600 mx-auto mb-2" />
                      <p className="font-semibold">Bookings</p>
                    </a>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <a href="/admin/payouts" className="card hover:shadow-lg transition-shadow text-center">
                      <FaRupeeSign className="text-3xl text-yellow-600 mx-auto mb-2" />
                      <p className="font-semibold">Payouts</p>
                    </a>
                    <a href="/admin/analytics" className="card hover:shadow-lg transition-shadow text-center">
                      <FaChartLine className="text-3xl text-indigo-600 mx-auto mb-2" />
                      <p className="font-semibold">Analytics</p>
                    </a>
                    </div>
                  </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
